import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import {
  addUserByEmail,
  assignDevice,
  assignDeviceToCustomer,
  fetchAddableUsers,
  fetchAllCustomers,
  fetchAssignableDevices,
  fetchCompanyUsers,
  fetchCustomerPool,
  fetchMe,
  fetchUnassignedDevices,
  fetchUserDevices,
  removeUser,
  unassignDevice,
  unassignDeviceFromCustomer,
  type AddableUserItem,
  type CustomerSummary,
  type DeviceItem,
  type MeInfo,
  type UserDeviceItem,
  type UserListItem,
} from "../lib/users";

/**
 * Users tab — lists crew members in a company, lets a manager add (by email) /
 * remove them, and expand a row to manage that user's UserDevices.
 *
 * External users (`customer_id` set on their own row) are locked to their own
 * company — no picker. Internal Oso staff see a customer-picker dropdown so
 * they can manage any company.
 */
export function UsersPage() {
  const [me, setMe] = useState<MeInfo | null>(null);
  const [meError, setMeError] = useState<string | null>(null);

  useEffect(() => {
    fetchMe()
      .then(setMe)
      .catch((e) => setMeError(e.message));
  }, []);

  if (meError) return <ErrorNote>Couldn’t load your profile: {meError}</ErrorNote>;
  if (!me) return <Placeholder icon="ti-loader-2" spin>Loading your profile…</Placeholder>;

  if (!me.can_manage_users) {
    return (
      <Placeholder icon="ti-lock" title="No company linked">
        Your account isn’t linked to a company yet. Ask Oso to link it to a
        customer and this tab will start working.
      </Placeholder>
    );
  }

  if (me.user_type === "internal") {
    return <InternalUsersPanel />;
  }
  // External manager: locked to their own customer.
  return (
    <div className="users-page">
      <UsersTable activeCustomerId={me.customer_id!} canPickCustomer={false} />
    </div>
  );
}

/** Internal-only view: lets the staff member pick a customer, then renders
 *  the same users table scoped to that customer. */
function InternalUsersPanel() {
  const [customers, setCustomers] = useState<CustomerSummary[] | null>(null);
  const [selected, setSelected] = useState<number | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    fetchAllCustomers()
      .then((cs) => {
        setCustomers(cs);
        if (cs.length > 0) setSelected(cs[0].id);
      })
      .catch((e) => setErr(e.message));
  }, []);

  if (err) return <ErrorNote>Couldn’t load customers: {err}</ErrorNote>;
  if (customers == null)
    return <Placeholder icon="ti-loader-2" spin>Loading customers…</Placeholder>;
  if (customers.length === 0) {
    return (
      <Placeholder icon="ti-building" title="No customers yet">
        Create one in the field-support Admin Portal, then come back.
      </Placeholder>
    );
  }

  const active = customers.find((c) => c.id === selected);

  return (
    <div className="users-page">
      <div className="users-toolbar">
        <div className="users-toolbar-id">
          <span className="users-avatar users-avatar-co" aria-hidden="true">
            <i className="ti ti-building" />
          </span>
          <div>
            <div className="users-toolbar-name">{active?.name ?? "Select a customer"}</div>
            <div className="users-toolbar-sub">
              Managing crew and device access
              {active ? ` · customer #${active.id}` : ""}
            </div>
          </div>
        </div>
        <label className="ss-filter">
          <span>Customer</span>
          <select
            id="cust-picker"
            value={selected ?? ""}
            onChange={(e) => setSelected(Number(e.target.value))}
          >
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
      </div>
      {selected != null && (
        <Fragment key={selected /* remount on customer switch to reset state */}>
          <CustomerDevicesPanel customerId={selected} />
          <UsersTable activeCustomerId={selected} canPickCustomer={true} />
        </Fragment>
      )}
    </div>
  );
}

/** Internal-only: manage which devices belong to a customer's pool. Devices
 *  added here become assignable to that customer's crew (in UsersTable below).
 *  A device can be in only one customer's pool at a time. */
function CustomerDevicesPanel({ customerId }: { customerId: number }) {
  const [pool, setPool] = useState<DeviceItem[] | null>(null);
  const [available, setAvailable] = useState<DeviceItem[] | null>(null);
  const [picked, setPicked] = useState<string>("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    setErr(null);
    try {
      const [p, av] = await Promise.all([
        fetchCustomerPool(customerId),
        fetchUnassignedDevices(),
      ]);
      setPool(p);
      setAvailable(av);
      // Start empty so the select reads "Choose a device…" rather than looking
      // like a device is already staged for adding.
      setPicked("");
    } catch (e) {
      setErr((e as Error).message);
    }
  }, [customerId]);

  useEffect(() => {
    reload();
  }, [reload]);

  const handleAssign = async () => {
    if (!picked) return;
    setBusy(true);
    setErr(null);
    try {
      await assignDeviceToCustomer(customerId, picked);
      await reload();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const handleUnassign = async (deviceId: string) => {
    setBusy(true);
    setErr(null);
    try {
      await unassignDeviceFromCustomer(customerId, deviceId);
      await reload();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="cd users-card">
      <div className="users-card-head">
        <p className="ct">
          DEVICES
          {pool && <span className="users-count">{pool.length}</span>}
        </p>
        <span className="users-card-hint">
          Devices this customer owns — what their crew can be assigned from.
        </span>
      </div>

      {err && <ErrorNote>{err}</ErrorNote>}

      {pool == null ? (
        <div className="muted users-inline-note">Loading…</div>
      ) : pool.length === 0 ? (
        <div className="muted users-inline-note">
          No devices in this pool yet. Add one below — the crew can then be
          assigned to it after signing in.
        </div>
      ) : (
        <ul className="device-chips">
          {pool.map((d) => (
            <li key={d.device_id}>
              <i className="ti ti-device-remote" aria-hidden="true" />
              <span className="device-id">{d.device_id}</span>
              {d.display_name && <span className="muted">{d.display_name}</span>}
              <button
                className="users-row-btn chip-btn"
                disabled={busy}
                onClick={() => handleUnassign(d.device_id)}
                title={`Remove ${d.device_id} from this customer`}
                aria-label={`Remove ${d.device_id} from this customer`}
              >
                <i className="ti ti-x" aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="device-assign-row">
        <select
          className={picked ? undefined : "is-placeholder"}
          value={picked}
          onChange={(e) => setPicked(e.target.value)}
          disabled={busy || !available || available.length === 0}
          aria-label="Unassigned device to add"
        >
          {(available || []).length === 0 ? (
            <option value="">No unassigned devices</option>
          ) : (
            <>
              <option value="">Choose a device…</option>
              {available!.map((d) => (
                <option key={d.device_id} value={d.device_id}>
                  {d.device_id}
                  {d.display_name ? ` — ${d.display_name}` : ""}
                </option>
              ))}
            </>
          )}
        </select>
        <button className="users-btn" onClick={handleAssign} disabled={busy || !picked}>
          <i className="ti ti-plus" aria-hidden="true" /> Add device
        </button>
      </div>
    </section>
  );
}

function UsersTable({
  activeCustomerId,
  canPickCustomer,
}: {
  activeCustomerId: number;
  /** True when the caller is internal — affects which `?customer_id=` we send.
   *  External callers must omit it (backend rejects mismatches). */
  canPickCustomer: boolean;
}) {
  const cid = canPickCustomer ? activeCustomerId : undefined;

  const [users, setUsers] = useState<UserListItem[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState("");
  const [query, setQuery] = useState("");
  /** Internal-only picker: everyone not already in this company. `null` while
   *  loading or when the caller is external (no such endpoint for them). */
  const [candidates, setCandidates] = useState<AddableUserItem[] | null>(null);

  const reloadCandidates = useCallback(() => {
    if (!canPickCustomer) return;
    fetchAddableUsers(activeCustomerId)
      .then(setCandidates)
      // A failed picker shouldn't break the roster — the email field still works.
      .catch(() => setCandidates([]));
  }, [canPickCustomer, activeCustomerId]);

  const reload = useCallback(() => {
    setLoadError(null);
    fetchCompanyUsers(cid)
      .then(setUsers)
      .catch((e) => setLoadError(e.message));
    reloadCandidates();
  }, [cid, reloadCandidates]);

  useEffect(reload, [reload]);

  const { unattached, elsewhere } = useMemo(
    () => ({
      unattached: (candidates ?? []).filter((c) => c.customer_id == null),
      elsewhere: (candidates ?? []).filter((c) => c.customer_id != null),
    }),
    [candidates],
  );
  /** Keep the select in sync with the email field (and reset it after an add)
   *  instead of tracking a second copy of the same choice. */
  const pickedEmail = useMemo(() => {
    const e = email.trim().toLowerCase();
    // Return the candidate's own spelling — that's the <option> value, so a
    // differently-cased typed address still selects the right row.
    return (candidates ?? []).find((c) => c.email.toLowerCase() === e)?.email ?? "";
  }, [candidates, email]);

  const visible = useMemo(() => {
    if (!users) return null;
    const q = query.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) =>
      `${u.name ?? ""} ${u.email ?? ""}`.toLowerCase().includes(q),
    );
  }, [users, query]);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    setActionError(null);
    try {
      await addUserByEmail(email.trim(), cid);
      setEmail("");
      reload();
    } catch (err) {
      setActionError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async (userOid: string, who: string) => {
    if (!window.confirm(`Remove ${who} from this company?`)) return;
    setBusy(true);
    setActionError(null);
    try {
      await removeUser(userOid, cid);
      if (expanded === userOid) setExpanded(null);
      reload();
    } catch (err) {
      setActionError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="cd users-card">
      <div className="users-card-head">
        <p className="ct">
          CREW
          {users && (
            <span className="users-count">
              {visible && visible.length !== users.length
                ? `${visible.length} / ${users.length}`
                : users.length}
            </span>
          )}
        </p>
        {users && users.length > 0 && (
          <label className="ss-filter users-search">
            <i className="ti ti-search" aria-hidden="true" />
            <input
              type="search"
              placeholder="Search name or email"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </label>
        )}
      </div>

      <form className="users-add-form" onSubmit={handleAdd}>
        <i className="ti ti-mail" aria-hidden="true" />
        <input
          type="email"
          placeholder="crew@company.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={busy}
          aria-label="Email of the crew member to add"
        />
        {canPickCustomer && (
          <select
            className={
              "users-add-picker" + (pickedEmail ? "" : " is-placeholder")
            }
            value={pickedEmail}
            onChange={(e) => setEmail(e.target.value)}
            disabled={busy || candidates == null || candidates.length === 0}
            aria-label="Pick an existing user by email"
          >
            {candidates != null && candidates.length === 0 ? (
              <option value="">No other users</option>
            ) : (
              <>
                <option value="">Pick a user…</option>
                {unattached.length > 0 && (
                  <optgroup label="Not in a company">
                    {unattached.map((c) => (
                      <option key={c.user_oid} value={c.email}>
                        {c.email}
                        {c.name ? ` — ${c.name}` : ""}
                      </option>
                    ))}
                  </optgroup>
                )}
                {elsewhere.length > 0 && (
                  <optgroup label="In another company">
                    {elsewhere.map((c) => (
                      <option key={c.user_oid} value={c.email}>
                        {c.email} — {c.customer_name ?? `customer #${c.customer_id}`}
                      </option>
                    ))}
                  </optgroup>
                )}
              </>
            )}
          </select>
        )}
        <button className="users-btn" type="submit" disabled={busy || !email.trim()}>
          <i className="ti ti-user-plus" aria-hidden="true" /> Add crew
        </button>
      </form>
      <p className="users-form-hint">
        They must have signed in to the mobile app at least once so their
        account exists.
        {canPickCustomer &&
          " Anyone already in another company must be removed there first."}
      </p>

      {actionError && <ErrorNote>{actionError}</ErrorNote>}
      {loadError && <ErrorNote>Couldn’t load users: {loadError}</ErrorNote>}

      {users == null ? (
        <div className="muted users-inline-note">Loading…</div>
      ) : users.length === 0 ? (
        <Placeholder icon="ti-users" title="No crew yet" flush>
          Add someone by email above to give them access to this company’s
          mowers.
        </Placeholder>
      ) : visible!.length === 0 ? (
        <div className="muted users-inline-note">
          No one matches “{query}”.
        </div>
      ) : (
        <table className="ft users-table">
          <thead>
            <tr>
              <th style={{ width: "34%" }}>NAME</th>
              <th style={{ width: "30%" }}>EMAIL</th>
              <th style={{ width: "12%" }}>PROVIDER</th>
              <th style={{ width: "10%" }}>DEVICES</th>
              <th style={{ width: "14%" }}>LAST LOGIN</th>
              <th style={{ width: 44 }} aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {visible!.map((u) => {
              const isOpen = expanded === u.user_oid;
              const label = u.name || u.email || u.user_oid;
              return (
                <Fragment key={u.user_oid}>
                  <tr
                    className={"users-row" + (isOpen ? " open" : "")}
                    onClick={() => setExpanded(isOpen ? null : u.user_oid)}
                    title={isOpen ? "Hide devices" : "Manage devices"}
                  >
                    <td>
                      <div className="users-name-cell">
                        <span className="users-avatar" aria-hidden="true">
                          {initials(label)}
                        </span>
                        <span className="users-name">
                          {u.name || <span className="muted">Unnamed user</span>}
                        </span>
                        <i
                          className={
                            "ti ti-chevron-right users-chevron" + (isOpen ? " open" : "")
                          }
                          aria-hidden="true"
                        />
                      </div>
                    </td>
                    <td className="users-truncate" title={u.email || undefined}>
                      {u.email || <span className="muted">—</span>}
                    </td>
                    <td>
                      {u.provider ? (
                        <span className="users-tag">{u.provider}</span>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    <td>
                      <span
                        className={"users-pill" + (u.device_count === 0 ? " zero" : "")}
                      >
                        <i className="ti ti-device-remote" aria-hidden="true" />
                        {u.device_count}
                      </span>
                    </td>
                    <td className="muted">{formatLogin(u.last_login)}</td>
                    <td>
                      <button
                        className="users-row-btn"
                        title={`Remove ${label} from this company`}
                        aria-label={`Remove ${label} from this company`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemove(u.user_oid, u.email || u.name || u.user_oid);
                        }}
                      >
                        <i className="ti ti-user-minus" aria-hidden="true" />
                      </button>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="users-devices-row">
                      <td colSpan={6}>
                        <UserDevicesPanel
                          user={u}
                          customerId={cid}
                          onChanged={reload}
                        />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}

function UserDevicesPanel({
  user,
  customerId,
  onChanged,
}: {
  user: UserListItem;
  customerId?: number;
  onChanged: () => void;
}) {
  const [assigned, setAssigned] = useState<UserDeviceItem[] | null>(null);
  const [available, setAvailable] = useState<DeviceItem[] | null>(null);
  const [picked, setPicked] = useState<string>("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    setErr(null);
    try {
      const [a, av] = await Promise.all([
        fetchUserDevices(user.user_oid, customerId),
        fetchAssignableDevices(user.user_oid, customerId),
      ]);
      setAssigned(a);
      setAvailable(av);
      // Empty by default — see CustomerDevicesPanel.
      setPicked("");
    } catch (e) {
      setErr((e as Error).message);
    }
  }, [user.user_oid, customerId]);

  useEffect(() => {
    reload();
  }, [reload]);

  const handleAssign = async () => {
    if (!picked) return;
    setBusy(true);
    setErr(null);
    try {
      await assignDevice(user.user_oid, picked, customerId);
      await reload();
      onChanged();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const handleUnassign = async (deviceId: string) => {
    setBusy(true);
    setErr(null);
    try {
      await unassignDevice(user.user_oid, deviceId, customerId);
      await reload();
      onChanged();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="user-devices">
      <div className="user-devices-header">
        <i className="ti ti-device-remote" aria-hidden="true" /> Devices assigned to{" "}
        <strong>{user.name || user.email || user.user_oid}</strong>
      </div>
      {err && <ErrorNote>{err}</ErrorNote>}

      {assigned == null ? (
        <div className="muted">Loading…</div>
      ) : assigned.length === 0 ? (
        <div className="muted">
          No devices assigned yet — pick one below to give them access.
        </div>
      ) : (
        <ul className="device-chips">
          {assigned.map((d) => (
            <li key={d.device_id}>
              <i className="ti ti-device-remote" aria-hidden="true" />
              <span className="device-id">{d.device_id}</span>
              {d.device_display_name && (
                <span className="muted">{d.device_display_name}</span>
              )}
              <button
                className="users-row-btn chip-btn"
                disabled={busy}
                onClick={() => handleUnassign(d.device_id)}
                title={`Unassign ${d.device_id}`}
                aria-label={`Unassign ${d.device_id}`}
              >
                <i className="ti ti-x" aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="device-assign-row">
        <select
          className={picked ? undefined : "is-placeholder"}
          value={picked}
          onChange={(e) => setPicked(e.target.value)}
          disabled={busy || !available || available.length === 0}
          aria-label="Device to assign"
        >
          {(available || []).length === 0 ? (
            <option value="">No assignable devices</option>
          ) : (
            <>
              <option value="">Choose a device…</option>
              {available!.map((d) => (
                <option key={d.device_id} value={d.device_id}>
                  {d.device_id}
                  {d.display_name ? ` — ${d.display_name}` : ""}
                </option>
              ))}
            </>
          )}
        </select>
        <button className="users-btn" onClick={handleAssign} disabled={busy || !picked}>
          <i className="ti ti-plus" aria-hidden="true" /> Assign
        </button>
      </div>
    </div>
  );
}

// --- small presentational helpers -------------------------------------------

function initials(label: string): string {
  const parts = label.replace(/@.*$/, "").split(/[\s._-]+/).filter(Boolean);
  if (parts.length === 0) return "?";
  return (parts[0][0] + (parts[1]?.[0] ?? "")).toUpperCase();
}

function formatLogin(iso: string | null): string {
  if (!iso) return "Never";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function ErrorNote({ children }: { children: React.ReactNode }) {
  return (
    <div className="users-error">
      <i className="ti ti-alert-triangle" aria-hidden="true" />
      <span>{children}</span>
    </div>
  );
}

function Placeholder({
  icon,
  title,
  children,
  spin,
  flush,
}: {
  icon: string;
  title?: string;
  children: React.ReactNode;
  spin?: boolean;
  /** Rendered inside an existing card — drop the card chrome. */
  flush?: boolean;
}) {
  return (
    <div className={"users-empty" + (flush ? " flush" : "")}>
      <i className={`ti ${icon}${spin ? " spin" : ""}`} aria-hidden="true" />
      {title && <div className="users-empty-title">{title}</div>}
      <div>{children}</div>
    </div>
  );
}
